import {useEffect, useState} from 'react'
import {cancelCircleIcon, clockIcon, fileIcon, notificationBellIcon, successNotification, notificationDone, magnifyingIcon, closeIcon, arrowRightWhite} from '@img/index.js'
import {Tab, TabGroup, TabList, TabPanel, TabPanels} from '@headlessui/react'
import {arrowRightSm} from "@img"
import {useNotifications, useMarkAsRead, useMarkAllAsRead, useDeleteAllNotifications} from '@/hooks/useNotifications'
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {useNavigate, useSearchParams} from 'react-router'
import {AnimatePresence, motion} from 'framer-motion'
import {formatTimeAgo, getPageNumbers} from '@/utils/functions'
import {parseAppZoneDate, appZoneStartOfDay} from '@/utils/dateUtils'

const PER_PAGE = 10

const fadeScale = {
    initial: {opacity: 0, scale: 0.95},
    animate: {opacity: 1, scale: 1},
    exit: {opacity: 0, scale: 0.95},
    transition: {duration: 0.2, ease: 'easeOut'},
}

function getNotificationIcon(category) {
    const raw = Array.isArray(category) ? category[0] : category
    const slug = (raw == null ? '' : String(raw)).toLowerCase()
    if (slug.includes('cancel')) return cancelCircleIcon
    if (slug.includes('operational')) return clockIcon
    if (slug.includes('marketing')) return notificationBellIcon
    if (slug.includes('success')) return successNotification
    return fileIcon
}

// "Today" means today in APP_TIMEZONE, not in the visitor's zone — delivered_at is a
// naive Toronto wall-clock stamp, and both sides of the comparison have to agree on
// which calendar day that is. (`new Date(dateStr)` read it as browser-local, which put
// evening notifications in the wrong bucket for anyone outside that zone.)
function getDateGroup(dateStr) {
    if (!dateStr) return 'Earlier'
    const date = parseAppZoneDate(dateStr)
    if (!date || isNaN(date)) return 'Earlier'
    const diffDays = Math.floor((appZoneStartOfDay(new Date()) - appZoneStartOfDay(date)) / 86400000)

    if (diffDays === 0) return 'Today'
    if (diffDays < 7) return 'This Week'
    return 'Earlier'
}

function groupNotifications(notifications) {
    const order = ['Today', 'This Week', 'Earlier']
    const grouped = {}

    notifications.forEach(n => {
        const label = getDateGroup(n.delivered_at)
        if (!grouped[label]) grouped[label] = []
        grouped[label].push(n)
    })

    return order
        .filter(label => grouped[label]?.length > 0)
        .map(label => ({label, items: grouped[label]}))
}

function NotificationList({unreadOnly, page, setPage, search}) {
    const [markingIds, setMarkingIds] = useState(() => new Set())
    const {data, isLoading, isFetching} = useNotifications({page, perPage: PER_PAGE, unreadOnly, search})
    const markAsRead = useMarkAsRead()
    const markAllAsRead = useMarkAllAsRead()
    const deleteAll = useDeleteAllNotifications()
    const navigate = useNavigate()

    const notifications = data?.data || []
    const pagination = data?.pagination
    const totalPages = pagination?.last_page ?? page

    if (isLoading) {
        return (
            <div className="white-global-section">
                <div className="animate-pulse space-y-4">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="flex gap-4 items-center bg-gray-50 rounded-2xl px-5 py-4">
                            <div className="w-14 h-14 bg-gray-200 rounded-sm"></div>
                            <div className="flex-1 space-y-2">
                                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    if (!notifications || notifications.length === 0) {
        return (
            <div className="white-global-section">
                <p className="text-(--text-secondary) mb-0!">{search ? 'No notifications match your search' : 'No notifications'}</p>
            </div>
        )
    }

    const groups = groupNotifications(notifications)

    const handleMarkRead = (id) => {
        setMarkingIds(prev => {
            const next = new Set(prev)
            next.add(id)
            return next
        })
        markAsRead.mutate(id, {
            onError: () => {
                setMarkingIds(prev => {
                    const next = new Set(prev)
                    next.delete(id)
                    return next
                })
            },
        })
    }

    // Whole-card click → open the linked trip (marking it read on the way). Trip-less
    // notifications aren't clickable. The "Mark as read" button stops propagation so it
    // only marks read and never navigates.
    const openTrip = (item) => {
        if (!item.trip_id) return
        if (!item.is_read) handleMarkRead(item.id)
        navigate(`/rides/${item.trip_id}`)
    }

    const handleReadAll = () => {
        markAllAsRead.mutate()
    }

    const handleDeleteAll = () => {
        deleteAll.mutate(undefined, {
            onSuccess: () => setPage(1),
        })
    }

    const canPrev = page > 1
    const canNext = pagination ? page < pagination.last_page : false
    const hasUnread = notifications.some(n => !n.is_read)

    return (
        <>
            <div className={`flex flex-col gap-6 transition-opacity duration-200 ${isFetching ? 'opacity-60' : ''}`}>
                {groups.map((group) => (
                    <div key={group.label} className="white-global-section">
                        <div>
                            <h6 className="text-(--text-secondary) font-medium max-md:text-sm!">{group.label}</h6>
                            <div className="flex flex-col gap-3">
                                <AnimatePresence initial={false}>
                                    {group.items.map((item) => (
                                        <motion.div
                                            key={item.id}
                                            layout
                                            {...fadeScale}
                                            onClick={item.trip_id ? () => openTrip(item) : undefined}
                                            role={item.trip_id ? 'link' : undefined}
                                            tabIndex={item.trip_id ? 0 : undefined}
                                            onKeyDown={item.trip_id ? (e) => { if (e.key === 'Enter') openTrip(item) } : undefined}
                                            className={`flex items-center justify-between gap-4 rounded-3xl px-5 py-4 shadow-sm max-md:flex-col max-md:items-stretch max-md:gap-2 max-md:rounded-2xl max-md:px-3 max-md:py-3 bg-(--bg-grey) ${item.trip_id ? 'cursor-pointer' : ''}`}>
                                            <div className="flex items-center gap-5 flex-1 min-w-0 max-md:w-full max-md:gap-3">
                                                <span className="w-14 h-14 bg-(--bg-mid-grey) rounded-xl flex items-center justify-center shrink-0 max-md:w-10 max-md:h-10">
                                                    <img src={getNotificationIcon(item.notification_categories)} alt="" className="w-5 h-5 max-md:w-4 max-md:h-4"/>
                                                </span>
                                                <div className="min-w-0 text-(--text-secondary)">
                                                    <h6 className="font-bold max-md:text-sm! max-md:mb-1!">
                                                        {item.rendered_push_title || item.notification_name}
                                                    </h6>
                                                    <p className="mb-0! max-md:text-xs">{item.rendered_push_body || ''}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 shrink-0 max-md:w-full">
                                                <span className="text-xs text-(--text-secondary) whitespace-nowrap max-md:mr-auto">{formatTimeAgo(item.delivered_at)}</span>
                                                {!item.is_read && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleMarkRead(item.id) }}
                                                        disabled={markingIds.has(item.id)}
                                                        className="cursor-pointer bg-(--bg-dark) text-white font-medium px-5.5 py-2.5 rounded-full whitespace-nowrap min-w-32 max-md:min-w-0 max-md:px-4 max-md:py-1.5 max-md:text-xs flex items-center justify-center disabled:opacity-50 disabled:cursor-default">
                                                        <AnimatePresence mode="wait" initial={false}>
                                                            {markingIds.has(item.id) ? (
                                                                <motion.span key="loader" {...fadeScale} className="flex items-center">
                                                                    <Loader color="#fff"/>
                                                                </motion.span>
                                                            ) : (
                                                                <motion.span key="label" {...fadeScale}>Mark as read</motion.span>
                                                            )}
                                                        </AnimatePresence>
                                                    </button>
                                                )}
                                                {item.trip_id && (
                                                    <img src={item.is_read ? notificationDone : arrowRightSm} alt=""/>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {(canPrev || canNext || notifications.length > 0) && (
                <div className="flex flex-col items-stretch gap-4 mt-8 md:flex-row md:items-center md:gap-0">
                    {(canPrev || canNext) && (
                        <div className="flex items-center gap-2 max-md:gap-1 flex-1 justify-center">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={!canPrev}
                                className="cursor-pointer w-9 h-9 rounded-full flex items-center justify-center max-md:h-7 max-md:w-7 max-md:text-sm disabled:opacity-30 disabled:cursor-default"
                            >
                                <svg width="8" height="12" viewBox="0 0 8 12" fill="none"><path d="M6.5 1L1.5 6L6.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </button>
                            {getPageNumbers(page, totalPages).map((p, i) => (
                                p === '...'
                                    ? <span key={`dots-${i}`} className="w-9 h-9 flex items-center justify-center text-(--text-secondary) max-md:h-7 max-md:w-7 max-md:text-sm">...</span>
                                    : <button
                                        key={p}
                                        onClick={() => setPage(p)}
                                        className={`cursor-pointer w-9 h-9 rounded-full flex items-center justify-center max-md:h-7 max-md:w-7 max-md:text-sm font-medium ${p === page ? 'bg-(--bg-dark) text-white' : ''}`}
                                    >
                                        {p}
                                    </button>
                            ))}
                            <button
                                onClick={() => setPage(p => p + 1)}
                                disabled={!canNext}
                                className="cursor-pointer w-9 h-9 rounded-full flex items-center justify-center max-md:h-7 max-md:w-7 max-md:text-sm disabled:opacity-30 disabled:cursor-default"
                            >
                                <svg width="8" height="12" viewBox="0 0 8 12" fill="none"><path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </button>
                        </div>
                    )}
                    {notifications.length > 0 && (
                        <div className="flex items-center gap-3 shrink-0 max-md:w-full max-md:justify-center md:ml-auto">
                            {hasUnread && (
                                <button
                                    onClick={handleReadAll}
                                    disabled={markAllAsRead.isPending}
                                    className="bg-(--text-primary) cursor-pointer font-medium text-white rounded-full px-13 py-2 min-w-32 flex items-center justify-center max-md:px-6 max-md:py-1.5 max-md:text-sm">
                                    {markAllAsRead.isPending ? <Loader color="#fff"/> : 'Read All'}
                                </button>
                            )}
                            <button
                                onClick={handleDeleteAll}
                                disabled={deleteAll.isPending}
                                className="border border-(--text-primary) cursor-pointer font-medium text-(--text-primary) rounded-full px-13 py-2 min-w-32 flex items-center justify-center max-md:px-6 disabled:opacity-50">
                                {deleteAll.isPending ? <Loader color="#000"/> : 'Delete All'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    )
}

export default function Notifications() {
    const [searchParams, setSearchParams] = useSearchParams()
    const selectedIndex = searchParams.get('tab') === 'unread' ? 1 : 0
    const page = Math.max(1, parseInt(searchParams.get('page'), 10) || 1)
    const urlSearch = searchParams.get('q') || ''
    const [searchInput, setSearchInput] = useState(urlSearch)

    useEffect(() => {
        setSearchInput(urlSearch)
    }, [urlSearch])

    const commitSearch = (value) => {
        const params = new URLSearchParams(searchParams)
        if (value) params.set('q', value)
        else params.delete('q')
        params.delete('page')
        setSearchParams(params, {replace: true})
    }

    const setSelectedIndex = (idx) => {
        const params = new URLSearchParams()
        if (idx === 1) params.set('tab', 'unread')
        if (urlSearch) params.set('q', urlSearch)
        setSearchParams(params, {replace: true})
    }
    const setPage = (next) => {
        const params = new URLSearchParams(searchParams)
        if (next > 1) params.set('page', String(next))
        else params.delete('page')
        setSearchParams(params, {replace: true})
    }

    return (
        <div className="flex flex-col gap-5">
            <h2 className="max-md:text-xl!">Notifications</h2>

            <TabGroup selectedIndex={selectedIndex} onChange={setSelectedIndex}>
                <div className="flex items-center gap-4 flex-wrap">
                    <TabList className="flex items-center gap-2">
                        <Tab className={({selected}) => `cursor-pointer px-6 py-1 text-xl font-medium outline-none border-b-4 max-md:px-4 max-md:text-base ${selected ? 'font-bold text-(--text-primary) border-b-[var(--bg-grey)]' : 'text-(--text-secondary) border-b-transparent'}`}>
                            All
                        </Tab>
                        <Tab className={({selected}) => `cursor-pointer px-6 py-1 text-xl font-medium outline-none border-b-4 max-md:px-4 max-md:text-base ${selected ? 'font-bold text-(--text-primary) border-b-[var(--bg-grey)]' : 'text-(--text-secondary) border-b-transparent'}`}>
                            Unread
                        </Tab>
                    </TabList>

                    <form
                        onSubmit={(e) => { e.preventDefault(); commitSearch(searchInput) }}
                        className="relative ml-auto w-full max-w-80 max-md:max-w-full">
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search"
                            className={`w-full bg-(--bg-grey) rounded-full pl-5 py-3 outline-none border-0 focus:outline-none ${searchInput ? 'pr-22' : 'pr-11'}`}
                        />
                        {searchInput ? (
                            <div className="absolute right-1.5 inset-y-0 flex items-center gap-1.5">
                                <button type="button" onClick={() => { setSearchInput(''); commitSearch('') }}
                                        aria-label="Clear search"
                                        className="cursor-pointer w-8 h-8 rounded-full inline-flex items-center justify-center bg-white border-0 p-0">
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                                        <path d="M1 1L9 9M9 1L1 9" stroke="#201e20" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                </button>
                                <button type="submit" aria-label="Search"
                                        className="cursor-pointer w-8 h-8 rounded-full inline-flex items-center justify-center bg-(--text-primary) border-0 p-0">
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                        <path d="M2 7H12M12 7L7 2M12 7L7 12" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                            </div>
                        ) : (
                            <span className="absolute right-4 inset-y-0 flex items-center pointer-events-none">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="opacity-60">
                                    <circle cx="7" cy="7" r="5" stroke="#201e20" strokeWidth="1.5"/>
                                    <path d="M11 11L14 14" stroke="#201e20" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </span>
                        )}
                    </form>
                </div>

                <TabPanels className="mt-5">
                    <motion.div
                        key={selectedIndex}
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        transition={{duration: 0.2, ease: 'easeOut'}}>
                        {selectedIndex === 0 ? (
                            <TabPanel static>
                                <NotificationList unreadOnly={false} page={page} setPage={setPage} search={urlSearch}/>
                            </TabPanel>
                        ) : (
                            <TabPanel static>
                                <NotificationList unreadOnly={true} page={page} setPage={setPage} search={urlSearch}/>
                            </TabPanel>
                        )}
                    </motion.div>
                </TabPanels>
            </TabGroup>
        </div>
    )
}
